import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, ShieldCheck, Clock, Users, Building2 } from 'lucide-react';
import { MOCK_SERVICES } from '../data';
import { imageUrl } from '../lib/images';

export default function Ejendomsservice() {
  const [service, setService] = useState(MOCK_SERVICES.find(s => s.url_slug === 'ejendomsservice'));

  useEffect(() => {
    async function fetchService() {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/services');
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          const found = data.find(s => s.slug.da === 'ejendomsservice' || s.url_slug === 'ejendomsservice');
          if (found) setService(found);
        }
      } catch (error) {
        console.error('Error fetching service:', error);
      }
    }
    fetchService();
  }, []);

  if (!service) return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" /></div>;

  return (
    <div className="pt-20">
      <Helmet>
        <title>Ejendomsservice til Boligforeninger & Erhverv | DK Grøn Service</title>
        <meta name="description" content="Pålidelig ejendomsservice og fast vedligeholdelse af grønne fællesarealer for boligforeninger, erhverv og kommuner. Skræddersyede serviceaftaler med fokus på kvalitet." />
        <meta property="og:title" content="Ejendomsservice til Boligforeninger & Erhverv | DK Grøn Service" />
        <meta property="og:description" content="Pålidelig ejendomsservice og fast vedligeholdelse af grønne fællesarealer for boligforeninger, erhverv og kommuner. Få en skræddersyet serviceaftale." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Header */}
      <section className="bg-emerald-900 py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="https://assets.auxodev.dk/assets/8a41795c-dcbb-4ba1-a6ae-e2582bf01d41/image00001.jpeg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.h1 
            className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {service.title.da}
          </motion.h1>
          <motion.p 
            className="text-xl text-emerald-100 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Stabil og professionel vedligeholdelse af jeres udearealer. Vi skaber rammerne for et flot udemiljø.
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20 items-start">
            <motion.div 
              className="space-y-8"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div 
                className="prose prose-lg prose-emerald text-emerald-900/80 leading-relaxed max-w-none"
                dangerouslySetInnerHTML={{ __html: service.description.da }}
              />
              
              <div className="bg-emerald-50 p-8 rounded-[2.5rem] border border-emerald-100 space-y-6">
                <h3 className="text-2xl font-bold text-emerald-950">Hvorfor vælge os?</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {[
                    { icon: ShieldCheck, title: 'Høj kvalitet', desc: 'Vi går aldrig på kompromis med resultatet.' },
                    { icon: Clock, title: 'Overholder aftaler', desc: 'Vi kommer som aftalt, hver gang.' },
                    { icon: Users, title: 'Personlig kontakt', desc: 'Du taler direkte med den udførende.' },
                    { icon: Building2, title: 'Bred erfaring', desc: 'Vi løser opgaver for alle typer kunder.' }
                  ].map((item, index) => (
                    <motion.div 
                      key={item.title} 
                      className="flex gap-4"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                    >
                      <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shrink-0 shadow-sm">
                        <item.icon className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <h4 className="font-bold text-emerald-950">{item.title}</h4>
                        <p className="text-sm text-emerald-800/60">{item.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="space-y-8 md:sticky md:top-32"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="aspect-square md:aspect-[4/5] rounded-[3rem] overflow-hidden shadow-2xl">
                <img
                  src={imageUrl(service.image[0], 'width=600,quality=80,format=auto')}
                  loading="lazy"
                  decoding="async"
                  alt={service.title.da}
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="bg-emerald-900 rounded-[2.5rem] p-10 text-white shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-800 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
                <h3 className="text-2xl font-bold mb-4 relative z-10">Skal vi give et bud på jeres ejendom?</h3>
                <p className="text-emerald-100/70 mb-8 relative z-10">
                  Vi kommer gerne forbi til en uforpligtende gennemgang af jeres arealer og giver et tilbud på en fast serviceaftale.
                </p>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 bg-white text-emerald-900 px-8 py-4 rounded-2xl font-bold hover:bg-emerald-50 transition-all relative z-10"
                >
                  Kontakt os i dag
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Areas of work */}
      <section className="py-24 bg-emerald-50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold text-emerald-950">Vi dækker alle behov</h2>
            <p className="text-lg text-emerald-800/70">
              Uanset om I er en lille boligforening eller en stor virksomhed, har vi udstyret og erfaringen til at løfte opgaven.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Boligforeninger',
                desc: 'Løbende pasning af fællesarealer, græsslåning og hækklipning, så beboerne altid har pæne udemiljøer.',
              },
              {
                title: 'Erhverv & Virksomheder',
                desc: 'Præsentable udearealer ved kontorer og lagerhaller skaber et godt førstehåndsindtryk for jeres kunder.',
              },
              {
                title: 'Offentlige institutioner',
                desc: 'Vedligeholdelse af grønne områder ved skoler, børnehaver og andre kommunale bygninger.',
              },
            ].map((item, index) => (
              <div 
                key={item.title} 
                className={`bg-white p-10 rounded-[2.5rem] border border-emerald-100 shadow-sm hover:shadow-md transition-shadow ${
                  index === 2 ? 'md:col-span-2 lg:col-span-1' : ''
                }`}
              >
                <h3 className="text-xl font-bold text-emerald-950 mb-4">{item.title}</h3>
                <p className="text-emerald-800/60 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
